var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getActiveDiagram } from './theme';
import { computeRoutePoints, getAnchor, collectObstaclesInParent } from './router';
import { getConnectorAnchors, getCardSideAnchor, planEdgeFanAnchors } from './coords';
import { createEdgeLabel } from './primitives';
import { quadraticPathD, quadraticToPoints, mermaidHorizontalLink, mermaidOrgBusPaths, pathThroughWaypoints } from './pathUtils';
/** Angle of segment from (x1,y1) to (x2,y2) in radians */
export function segmentAngle(x1, y1, x2, y2) {
    return Math.atan2(y2 - y1, x2 - x1);
}
/** Sleek filled arrowhead — wider base so it reads clearly on dark themes. */
export function arrowHeadPoints(tipX, tipY, angle, size) {
    if (size === void 0) { size = 14; }
    var half = size * 0.48;
    var back = size * 1.15;
    var bx = tipX - back * Math.cos(angle);
    var by = tipY - back * Math.sin(angle);
    var lx = bx + half * Math.sin(angle);
    var ly = by - half * Math.cos(angle);
    var rx = bx - half * Math.sin(angle);
    var ry = by + half * Math.cos(angle);
    return [tipX, tipY, lx, ly, rx, ry];
}
/** Open (V-shaped) arrow for associations */
export function openArrowPoints(tipX, tipY, angle, size) {
    if (size === void 0) { size = 14; }
    var half = size * 0.5;
    var back = size * 1.0;
    var bx = tipX - back * Math.cos(angle);
    var by = tipY - back * Math.sin(angle);
    return [
        bx + half * Math.sin(angle),
        by - half * Math.cos(angle),
        tipX,
        tipY,
        bx - half * Math.sin(angle),
        by + half * Math.cos(angle),
    ];
}
/** UML composition / aggregation diamond */
export function diamondPoints(tipX, tipY, angle, size) {
    if (size === void 0) { size = 12; }
    var len = size;
    var half = size * 0.45;
    var backX = tipX - len * Math.cos(angle);
    var backY = tipY - len * Math.sin(angle);
    var midX = tipX - (len * 0.5) * Math.cos(angle);
    var midY = tipY - (len * 0.5) * Math.sin(angle);
    var lx = midX + half * Math.sin(angle);
    var ly = midY - half * Math.cos(angle);
    var rx = midX - half * Math.sin(angle);
    var ry = midY + half * Math.cos(angle);
    return [tipX, tipY, lx, ly, backX, backY, rx, ry];
}
/** Shorten terminal segment so arrowhead does not overlap stroke */
export function shortenPathEnd(points, trim) {
    if (points.length < 4 || trim <= 0)
        return points.slice();
    var copy = points.slice();
    var n = copy.length;
    var x2 = copy[n - 2];
    var y2 = copy[n - 1];
    var x1 = copy[n - 4];
    var y1 = copy[n - 3];
    var len = Math.hypot(x2 - x1, y2 - y1);
    var t = Math.min(trim / Math.max(len, 1), 0.65);
    copy[n - 2] = x2 - (x2 - x1) * t;
    copy[n - 1] = y2 - (y2 - y1) * t;
    return copy;
}
/** Midpoint along polyline by arc length */
export function pathMidpoint(points) {
    return pathPointAt(points, 0.5);
}
/**
 * Point at fraction `t` (0..1) along polyline arc length, plus unit tangent.
 * Used to park edge labels near the source so they are not buried under target nodes.
 */
export function pathPointAt(points, t) {
    var e_1, _a;
    var _b, _c;
    if (points.length < 4) {
        return { x: (_b = points[0]) !== null && _b !== void 0 ? _b : 0, y: (_c = points[1]) !== null && _c !== void 0 ? _c : 0, tx: 1, ty: 0 };
    }
    var segs = [];
    var total = 0;
    for (var i = 0; i < points.length - 2; i += 2) {
        var x1 = points[i];
        var y1 = points[i + 1];
        var x2 = points[i + 2];
        var y2 = points[i + 3];
        var len_1 = Math.hypot(x2 - x1, y2 - y1);
        segs.push({ len: len_1, x1: x1, y1: y1, x2: x2, y2: y2 });
        total += len_1;
    }
    var target = Math.max(0, Math.min(1, t)) * total;
    var walked = 0;
    try {
        for (var segs_1 = __values(segs), segs_1_1 = segs_1.next(); !segs_1_1.done; segs_1_1 = segs_1.next()) {
            var s = segs_1_1.value;
            if (walked + s.len >= target || s === segs[segs.length - 1]) {
                var local = s.len > 0 ? (target - walked) / s.len : 0;
                var clamped = Math.max(0, Math.min(1, local));
                var dx_1 = s.x2 - s.x1;
                var dy_1 = s.y2 - s.y1;
                var len_2 = Math.hypot(dx_1, dy_1) || 1;
                return {
                    x: s.x1 + dx_1 * clamped,
                    y: s.y1 + dy_1 * clamped,
                    tx: dx_1 / len_2,
                    ty: dy_1 / len_2,
                };
            }
            walked += s.len;
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (segs_1_1 && !segs_1_1.done && (_a = segs_1.return)) _a.call(segs_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var last = segs[segs.length - 1];
    var dx = last.x2 - last.x1;
    var dy = last.y2 - last.y1;
    var len = Math.hypot(dx, dy) || 1;
    return { x: last.x2, y: last.y2, tx: dx / len, ty: dy / len };
}
function pointInsideObstacle(x, y, obstacles, pad) {
    var e_2, _a;
    if (pad === void 0) { pad = 10; }
    try {
        for (var obstacles_1 = __values(obstacles), obstacles_1_1 = obstacles_1.next(); !obstacles_1_1.done; obstacles_1_1 = obstacles_1.next()) {
            var o = obstacles_1_1.value;
            if (x >= o.x - pad &&
                x <= o.x + o.width + pad &&
                y >= o.y - pad &&
                y <= o.y + o.height + pad) {
                return true;
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (obstacles_1_1 && !obstacles_1_1.done && (_a = obstacles_1.return)) _a.call(obstacles_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return false;
}
/**
 * Pick a label position along the path that stays outside node boxes when possible.
 * Tries several arc fractions + perpendicular offsets (keeps "no" visible past Process).
 */
export function placeEdgeLabelPoint(points, obstacles, preferredT, preferredOffset) {
    var e_3, _a, e_4, _b;
    if (preferredT === void 0) { preferredT = 0.36; }
    if (preferredOffset === void 0) { preferredOffset = 0; }
    var ts = [preferredT, 0.2, 0.28, 0.42, 0.15, 0.55];
    var offs = [
        preferredOffset,
        preferredOffset === 0 ? -14 : -preferredOffset,
        preferredOffset + 12,
        -(Math.abs(preferredOffset) + 12),
        18,
        -18,
        0,
    ];
    try {
        for (var ts_1 = __values(ts), ts_1_1 = ts_1.next(); !ts_1_1.done; ts_1_1 = ts_1.next()) {
            var t = ts_1_1.value;
            var at = pathPointAt(points, t);
            var nx = -at.ty;
            var ny = at.tx;
            try {
                for (var offs_1 = (e_4 = void 0, __values(offs)), offs_1_1 = offs_1.next(); !offs_1_1.done; offs_1_1 = offs_1.next()) {
                    var off = offs_1_1.value;
                    var x = at.x + nx * off;
                    var y = at.y + ny * off - 4;
                    if (!pointInsideObstacle(x, y, obstacles, 12)) {
                        return { x: x, y: y };
                    }
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (offs_1_1 && !offs_1_1.done && (_b = offs_1.return)) _b.call(offs_1);
                }
                finally { if (e_4) throw e_4.error; }
            }
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (ts_1_1 && !ts_1_1.done && (_a = ts_1.return)) _a.call(ts_1);
        }
        finally { if (e_3) throw e_3.error; }
    }
    var fallback = pathPointAt(points, preferredT);
    return {
        x: fallback.x - fallback.ty * (preferredOffset || -16),
        y: fallback.y + fallback.tx * (preferredOffset || -16) - 4,
    };
}
function addArrowMarker(app, group, style, tipX, tipY, angle, stroke, strokeWidth, arrowSize) {
    if (style === 'none')
        return;
    if (style === 'filled') {
        group.add(app.polygon({
            points: arrowHeadPoints(tipX, tipY, angle, arrowSize),
            fill: stroke,
            stroke: stroke,
            strokeWidth: Math.max(getActiveDiagram().stroke.arrow, 1.5),
            listening: false,
            zIndex: 5,
        }));
        return;
    }
    if (style === 'open') {
        group.add(app.polyline({
            points: openArrowPoints(tipX, tipY, angle, arrowSize),
            fill: null,
            stroke: stroke,
            strokeWidth: Math.max(strokeWidth + 0.5, 2.25),
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
            zIndex: 5,
        }));
        return;
    }
    if (style === 'hollow') {
        // UML generalization: hollow triangle filled with surface so it reads on any theme
        group.add(app.polygon({
            points: arrowHeadPoints(tipX, tipY, angle, arrowSize + 3),
            fill: getActiveDiagram().surface,
            stroke: stroke,
            strokeWidth: Math.max(getActiveDiagram().stroke.node, 1.75),
            listening: false,
        }));
        return;
    }
    if (style === 'diamond' || style === 'diamondHollow') {
        group.add(app.polygon({
            points: diamondPoints(tipX, tipY, angle, arrowSize + 2),
            fill: style === 'diamond' ? stroke : getActiveDiagram().surface,
            stroke: stroke,
            strokeWidth: getActiveDiagram().stroke.node,
            listening: false,
        }));
    }
}
/** Build a connector group: routed path + arrowheads + optional label */
export function createConnector(app, x1, y1, x2, y2, options) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    if (options === void 0) { options = {}; }
    var stroke = (_a = options.stroke) !== null && _a !== void 0 ? _a : getActiveDiagram().edge;
    var strokeWidth = (_b = options.strokeWidth) !== null && _b !== void 0 ? _b : getActiveDiagram().stroke.edge;
    var glowColor = (_c = options.glowColor) !== null && _c !== void 0 ? _c : getActiveDiagram().edgeGlow;
    var useGlow = options.glow === true;
    var arrowEnd = (_d = options.arrowEnd) !== null && _d !== void 0 ? _d : 'filled';
    var arrowStart = (_e = options.arrowStart) !== null && _e !== void 0 ? _e : 'none';
    // Larger heads so they stay readable on dark canvases / dashed strokes
    var arrowSize = Math.max(13, Math.min(20, strokeWidth * 7 + 4));
    var style = (_f = options.style) !== null && _f !== void 0 ? _f : 'smart';
    var obstacles = (_g = options.obstacles) !== null && _g !== void 0 ? _g : [];
    var waypoints = (_j = (_h = options.waypoints) === null || _h === void 0 ? void 0 : _h.filter(function (w) { return Number.isFinite(w.x) && Number.isFinite(w.y); })) !== null && _j !== void 0 ? _j : [];
    var points = waypoints.length > 0
        ? pathThroughWaypoints(x1, y1, waypoints, x2, y2, 16)
        : computeRoutePoints(x1, y1, x2, y2, style, obstacles, options.cornerRadius, (_k = options.railOffset) !== null && _k !== void 0 ? _k : 0);
    var group = app.group({ listening: false });
    // Pull tip slightly off the node border so the head sits outside (visible), not buried in the stroke
    var tipPull = Math.max(4, strokeWidth + 2);
    var trimEnd = arrowEnd !== 'none' ? arrowSize * 0.92 + tipPull : 0;
    var trimStart = arrowStart !== 'none' ? arrowSize * 0.92 + tipPull : 0;
    var display = points;
    if (trimEnd > 0)
        display = shortenPathEnd(display, trimEnd);
    if (trimStart > 0 && display.length >= 4) {
        var x0 = display[0];
        var y0 = display[1];
        var x1s = display[2];
        var y1s = display[3];
        var len = Math.hypot(x1s - x0, y1s - y0);
        var t = Math.min(trimStart / Math.max(len, 1), 0.65);
        display[0] = x0 + (x1s - x0) * t;
        display[1] = y0 + (y1s - y0) * t;
    }
    if (useGlow) {
        group.add(app.polyline({
            points: display,
            fill: null,
            stroke: glowColor,
            strokeWidth: strokeWidth + Math.max(2, getActiveDiagram().stroke.edgeGlow * 0.65),
            lineJoin: 'round',
            lineCap: 'round',
            opacity: 0.45,
            listening: false,
        }));
    }
    group.add(app.polyline(__assign(__assign({ points: display, fill: null, stroke: stroke, strokeWidth: strokeWidth, lineJoin: 'round', lineCap: 'round' }, (options.dash ? { dash: options.dash } : {})), { listening: false, metadata: { edgeStrokePoly: true } })));
    var endAngle = segmentAngle(display[display.length - 4], display[display.length - 3], display[display.length - 2], display[display.length - 1]);
    var startAngle = segmentAngle(display[0], display[1], display[2], display[3]);
    var safeEndAngle = Number.isFinite(endAngle) ? endAngle : 0;
    var safeStartAngle = Number.isFinite(startAngle) ? startAngle : 0;
    // Tip sits just outside the target border (back along incoming direction)
    var tipX = x2 - Math.cos(safeEndAngle) * tipPull;
    var tipY = y2 - Math.sin(safeEndAngle) * tipPull;
    var startTipX = x1 + Math.cos(safeStartAngle) * tipPull;
    var startTipY = y1 + Math.sin(safeStartAngle) * tipPull;
    addArrowMarker(app, group, arrowEnd, tipX, tipY, safeEndAngle, stroke, strokeWidth, arrowSize);
    addArrowMarker(app, group, arrowStart, startTipX, startTipY, safeStartAngle + Math.PI, stroke, strokeWidth, arrowSize);
    if (options.label) {
        var labelT = (_l = options.labelT) !== null && _l !== void 0 ? _l : 0.36;
        var labelOff = (_m = options.labelOffset) !== null && _m !== void 0 ? _m : 0;
        var pathForLabel = display.length >= 4 ? display : points;
        var placed = placeEdgeLabelPoint(pathForLabel, obstacles, labelT, labelOff);
        var label = createEdgeLabel(app, options.label, placed.x, placed.y, (_o = options.labelColor) !== null && _o !== void 0 ? _o : stroke);
        label.zIndex = 8;
        group.add(label);
    }
    if (options.edgeId)
        group.metadata.edgeId = options.edgeId;
    if (options.fromId)
        group.metadata.edgeFrom = options.fromId;
    if (options.toId)
        group.metadata.edgeTo = options.toId;
    if (options.label)
        group.metadata.edgeLabel = options.label;
    group.metadata.edgeStroke = stroke;
    group.metadata.edgeStrokeWidth = strokeWidth;
    group.metadata.edgeGlow = glowColor;
    group.metadata.edgeStyle = style;
    group.metadata.edgeArrowEnd = arrowEnd;
    group.metadata.edgeArrowStart = arrowStart;
    if (options.dash)
        group.metadata.edgeDash = options.dash;
    if (waypoints.length > 0)
        group.metadata.edgeWaypoints = waypoints.map(function (w) { return ({ x: w.x, y: w.y }); });
    group.metadata.edgePoints = display.length >= 4 ? display.slice() : points.slice();
    return group;
}
/** Curved mind-map branch (quadratic sampled as polyline), no arrowheads. */
export function createCurvedConnector(app, x1, y1, x2, y2, options) {
    var _a, _b, _c;
    if (options === void 0) { options = {}; }
    var stroke = (_a = options.stroke) !== null && _a !== void 0 ? _a : getActiveDiagram().edge;
    var strokeWidth = (_b = options.strokeWidth) !== null && _b !== void 0 ? _b : getActiveDiagram().stroke.edge;
    var glowColor = (_c = options.glowColor) !== null && _c !== void 0 ? _c : getActiveDiagram().edgeGlow;
    var group = app.group({ listening: false });
    var dx = x2 - x1;
    var dy = y2 - y1;
    // Control point pulls the curve outward for an organic branch feel
    var cx = x1 + dx * 0.45 - dy * 0.18;
    var cy = y1 + dy * 0.45 + dx * 0.18;
    var points = quadraticToPoints(x1, y1, cx, cy, x2, y2, 18);
    if (options.glow !== false) {
        group.add(app.polyline({
            points: points,
            fill: null,
            stroke: glowColor,
            strokeWidth: strokeWidth + 3,
            lineCap: 'round',
            lineJoin: 'round',
            opacity: 0.4,
            listening: false,
        }));
    }
    group.add(app.polyline({
        points: points,
        fill: null,
        stroke: stroke,
        strokeWidth: strokeWidth,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    }));
    group.metadata.edgePoints = points;
    group.metadata.edgeStroke = stroke;
    group.metadata.edgePathD = quadraticPathD(x1, y1, cx, cy, x2, y2);
    return group;
}
/** Connect two diagram nodes with orthogonal (or curved) routing */
export function connectNodes(app, from, to, obstacles, options) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    if (options === void 0) { options = {}; }
    var parent = options.parent;
    var x1;
    var y1;
    var x2;
    var y2;
    var routeObstacles = obstacles;
    if (options.anchors) {
        x1 = options.anchors.x1;
        y1 = options.anchors.y1;
        x2 = options.anchors.x2;
        y2 = options.anchors.y2;
        if (parent && options.obstacleNodes && options.obstacleNodes.length > 0) {
            routeObstacles = collectObstaclesInParent(options.obstacleNodes, parent, [from, to]);
        }
    }
    else if (parent) {
        var anchors = getConnectorAnchors(from, to, parent, {
            fromAlong: options.fromAlong,
            toAlong: options.toAlong,
        });
        x1 = anchors.x1;
        y1 = anchors.y1;
        x2 = anchors.x2;
        y2 = anchors.y2;
        if (options.obstacleNodes && options.obstacleNodes.length > 0) {
            routeObstacles = collectObstaclesInParent(options.obstacleNodes, parent, [from, to]);
        }
    }
    else {
        var toB = to.getBounds();
        var anchor = getAnchor(from, toB.x + toB.width / 2, toB.y + toB.height / 2);
        var toAnchor = getAnchor(to, anchor.x, anchor.y);
        x1 = anchor.x;
        y1 = anchor.y;
        x2 = toAnchor.x;
        y2 = toAnchor.y;
    }
    return createConnector(app, x1, y1, x2, y2, __assign(__assign({ style: (_a = options.style) !== null && _a !== void 0 ? _a : 'smart', fromId: (_b = options.fromId) !== null && _b !== void 0 ? _b : (_c = from.metadata) === null || _c === void 0 ? void 0 : _c.diagramId, toId: (_d = options.toId) !== null && _d !== void 0 ? _d : (_e = to.metadata) === null || _e === void 0 ? void 0 : _e.diagramId, edgeId: (_f = options.edgeId) !== null && _f !== void 0 ? _f : "".concat((_j = (_g = options.fromId) !== null && _g !== void 0 ? _g : (_h = from.metadata) === null || _h === void 0 ? void 0 : _h.diagramId) !== null && _j !== void 0 ? _j : 'a', "-").concat((_m = (_k = options.toId) !== null && _k !== void 0 ? _k : (_l = to.metadata) === null || _l === void 0 ? void 0 : _l.diagramId) !== null && _m !== void 0 ? _m : 'b') }, options), { waypoints: options.waypoints, obstacles: routeObstacles }));
}
/**
 * Connect many node pairs with shared fan-out planning so wires that leave
 * or enter the same card side do not stack on top of each other.
 * Mind-map / org bus wiring should not use this helper.
 */
export function connectNodePairs(app, pairs, shared) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    var parent = shared.parent;
    var obstacleNodes = (_a = shared.obstacleNodes) !== null && _a !== void 0 ? _a : [];
    var baseObstacles = (_b = shared.obstacles) !== null && _b !== void 0 ? _b : [];
    var plan = planEdgeFanAnchors(pairs.map(function (p, i) {
        var _a, _b, _c, _d;
        return ({
            key: (_b = (_a = p.options) === null || _a === void 0 ? void 0 : _a.edgeId) !== null && _b !== void 0 ? _b : "".concat(String((_c = p.from.metadata) === null || _c === void 0 ? void 0 : _c.diagramId), "->").concat(String((_d = p.to.metadata) === null || _d === void 0 ? void 0 : _d.diagramId), "#").concat(i),
            from: p.from,
            to: p.to,
        });
    }), parent);
    var out = [];
    var labeledIndex = 0;
    for (var i = 0; i < pairs.length; i++) {
        var p = pairs[i];
        var key = (_d = (_c = p.options) === null || _c === void 0 ? void 0 : _c.edgeId) !== null && _d !== void 0 ? _d : "".concat(String((_e = p.from.metadata) === null || _e === void 0 ? void 0 : _e.diagramId), "->").concat(String((_f = p.to.metadata) === null || _f === void 0 ? void 0 : _f.diagramId), "#").concat(i);
        var fan = plan.get(key);
        var hasLabel = !!((_h = (_g = p.options) === null || _g === void 0 ? void 0 : _g.label) !== null && _h !== void 0 ? _h : shared.label);
        var labelOffset = (_k = (_j = p.options) === null || _j === void 0 ? void 0 : _j.labelOffset) !== null && _k !== void 0 ? _k : (hasLabel ? (labeledIndex % 2 === 0 ? -14 : 14) + Math.floor(labeledIndex / 2) * 4 : 0);
        if (hasLabel)
            labeledIndex += 1;
        var pairOpts = __assign(__assign(__assign({}, shared), p.options), { parent: parent, obstacleNodes: obstacleNodes, obstacles: baseObstacles, style: (_o = (_m = (_l = p.options) === null || _l === void 0 ? void 0 : _l.style) !== null && _m !== void 0 ? _m : shared.style) !== null && _o !== void 0 ? _o : 'smart', fromAlong: fan === null || fan === void 0 ? void 0 : fan.fromAlong, toAlong: fan === null || fan === void 0 ? void 0 : fan.toAlong, railOffset: (_r = (_p = fan === null || fan === void 0 ? void 0 : fan.railOffset) !== null && _p !== void 0 ? _p : (_q = p.options) === null || _q === void 0 ? void 0 : _q.railOffset) !== null && _r !== void 0 ? _r : 0, labelT: (_t = (_s = p.options) === null || _s === void 0 ? void 0 : _s.labelT) !== null && _t !== void 0 ? _t : 0.36, labelOffset: labelOffset, anchors: fan
                ? { x1: fan.x1, y1: fan.y1, x2: fan.x2, y2: fan.y2 }
                : undefined });
        out.push(connectNodes(app, p.from, p.to, baseObstacles, pairOpts));
    }
    return out;
}
/** Draw a clean stroke polyline (Mermaid-like — no glow). */
function addCleanStroke(app, group, points, stroke, strokeWidth) {
    if (points.length < 4)
        return;
    group.add(app.polyline({
        points: points,
        fill: null,
        stroke: stroke,
        strokeWidth: strokeWidth,
        lineJoin: 'round',
        lineCap: 'round',
        listening: false,
    }));
}
/** Wire org-chart with Mermaid flowchart-TB shared bus connectors */
export function wireOrgChartConnectors(app, root) {
    var e_5, _a, e_6, _b;
    var edgeLayer = root.children.find(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.diagramEdgeLayer; });
    if (edgeLayer) {
        try {
            for (var _c = __values(__spreadArray([], __read(edgeLayer.children), false)), _d = _c.next(); !_d.done; _d = _c.next()) {
                var child = _d.value;
                edgeLayer.remove(child);
                child.destroy();
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_5) throw e_5.error; }
        }
    }
    else {
        edgeLayer = app.group({ listening: false, zIndex: -10 });
        edgeLayer.metadata.diagramEdgeLayer = true;
        root.add(edgeLayer);
    }
    var orgRoots = root.children.filter(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.orgNode; });
    try {
        for (var orgRoots_1 = __values(orgRoots), orgRoots_1_1 = orgRoots_1.next(); !orgRoots_1_1.done; orgRoots_1_1 = orgRoots_1.next()) {
            var orgRoot = orgRoots_1_1.value;
            walkOrgBusConnect(app, root, orgRoot, edgeLayer);
        }
    }
    catch (e_6_1) { e_6 = { error: e_6_1 }; }
    finally {
        try {
            if (orgRoots_1_1 && !orgRoots_1_1.done && (_b = orgRoots_1.return)) _b.call(orgRoots_1);
        }
        finally { if (e_6) throw e_6.error; }
    }
}
function walkOrgBusConnect(app, root, node, edgeLayer) {
    var e_7, _a, e_8, _b, e_9, _c;
    var _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    var children = node.children.filter(function (c) { var _a, _b; return ((_a = c.metadata) === null || _a === void 0 ? void 0 : _a.orgNode) && c.visible && c !== ((_b = node.metadata) === null || _b === void 0 ? void 0 : _b.collapseIndicator); });
    if (children.length > 0) {
        var parentPt = getCardSideAnchor(node, root, 'bottom');
        var childPts = children.map(function (child) {
            var top = getCardSideAnchor(child, root, 'top');
            return { x: top.x, topY: top.y };
        });
        var stroke = (_h = (_e = (_d = node.metadata) === null || _d === void 0 ? void 0 : _d.orgBranchAccent) !== null && _e !== void 0 ? _e : (_g = (_f = children[0]) === null || _f === void 0 ? void 0 : _f.metadata) === null || _g === void 0 ? void 0 : _g.orgBranchAccent) !== null && _h !== void 0 ? _h : getActiveDiagram().orgEdge;
        var sw = getActiveDiagram().orgEdgeWidth;
        // Always draw filleted elbows (shared stem/bus overdraw is same stroke — looks smooth).
        var elbows = mermaidOrgBusPaths(parentPt.x, parentPt.y, childPts, 12).elbows;
        var g = app.group({ listening: false });
        try {
            for (var elbows_1 = __values(elbows), elbows_1_1 = elbows_1.next(); !elbows_1_1.done; elbows_1_1 = elbows_1.next()) {
                var elbow = elbows_1_1.value;
                addCleanStroke(app, g, elbow, stroke, sw);
            }
        }
        catch (e_7_1) { e_7 = { error: e_7_1 }; }
        finally {
            try {
                if (elbows_1_1 && !elbows_1_1.done && (_a = elbows_1.return)) _a.call(elbows_1);
            }
            finally { if (e_7) throw e_7.error; }
        }
        g.metadata.diagramOrgBus = true;
        edgeLayer.add(g);
        // Metadata stubs so the editor can discover parent→child edges
        var fromId = ((_k = (_j = node.metadata) === null || _j === void 0 ? void 0 : _j.diagramId) !== null && _k !== void 0 ? _k : (_l = node.metadata) === null || _l === void 0 ? void 0 : _l.orgName);
        try {
            for (var children_1 = __values(children), children_1_1 = children_1.next(); !children_1_1.done; children_1_1 = children_1.next()) {
                var child = children_1_1.value;
                var toId = ((_o = (_m = child.metadata) === null || _m === void 0 ? void 0 : _m.diagramId) !== null && _o !== void 0 ? _o : (_p = child.metadata) === null || _p === void 0 ? void 0 : _p.orgName);
                var childStroke = (_r = (_q = child.metadata) === null || _q === void 0 ? void 0 : _q.orgBranchAccent) !== null && _r !== void 0 ? _r : stroke;
                var stub = app.group({ listening: false });
                stub.metadata.edgeFrom = fromId;
                stub.metadata.edgeTo = toId;
                stub.metadata.edgeId = "org_".concat(fromId, "_").concat(toId);
                stub.metadata.edgeStyle = 'orthogonal';
                stub.metadata.edgeArrowEnd = 'none';
                stub.metadata.edgeStroke = childStroke;
                stub.metadata.edgeStrokeWidth = sw;
                edgeLayer.add(stub);
            }
        }
        catch (e_8_1) { e_8 = { error: e_8_1 }; }
        finally {
            try {
                if (children_1_1 && !children_1_1.done && (_b = children_1.return)) _b.call(children_1);
            }
            finally { if (e_8) throw e_8.error; }
        }
    }
    try {
        for (var children_2 = __values(children), children_2_1 = children_2.next(); !children_2_1.done; children_2_1 = children_2.next()) {
            var child = children_2_1.value;
            walkOrgBusConnect(app, root, child, edgeLayer);
        }
    }
    catch (e_9_1) { e_9 = { error: e_9_1 }; }
    finally {
        try {
            if (children_2_1 && !children_2_1.done && (_c = children_2.return)) _c.call(children_2);
        }
        finally { if (e_9) throw e_9.error; }
    }
}
/** Mermaid-style mind-map links (horizontal cubics on card edges — not subtree bounds). */
export function wireMindMapConnectors(app, group) {
    var e_10, _a;
    var _b, _c, _d, _e, _f, _g;
    if (group.children.length < 2)
        return;
    var center = group.children[0];
    var edges = app.group({ listening: false, zIndex: -10 });
    edges.metadata.diagramEdgeLayer = true;
    for (var i = 1; i < group.children.length; i++) {
        var branch = group.children[i];
        if ((_b = branch.metadata) === null || _b === void 0 ? void 0 : _b.diagramEdgeLayer)
            continue;
        var branchStroke = (_d = (_c = branch.metadata) === null || _c === void 0 ? void 0 : _c.mindBranchColor) !== null && _d !== void 0 ? _d : getActiveDiagram().mindBranch.stroke;
        var side = (_f = (_e = branch.metadata) === null || _e === void 0 ? void 0 : _e.mindSide) !== null && _f !== void 0 ? _f : 'right';
        // Face toward each other on the horizontal axis
        var centerSide = side === 'right' ? 'right' : 'left';
        var branchInner = side === 'right' ? 'left' : 'right';
        var branchOuter = side === 'right' ? 'right' : 'left';
        var from = getCardSideAnchor(center, group, centerSide);
        var to = getCardSideAnchor(branch, group, branchInner);
        var link = mermaidHorizontalLink(from.x, from.y, to.x, to.y, 24);
        edges.add(createPolylineLink(app, link, branchStroke, getActiveDiagram().stroke.edge + 1.25, false));
        var branchGroup = branch;
        try {
            for (var _h = (e_10 = void 0, __values(branchGroup.children)), _j = _h.next(); !_j.done; _j = _h.next()) {
                var leaf = _j.value;
                if (!((_g = leaf.metadata) === null || _g === void 0 ? void 0 : _g.diagramId))
                    continue;
                var leafInner = side === 'right' ? 'left' : 'right';
                var bOut = getCardSideAnchor(branch, group, branchOuter);
                var lIn = getCardSideAnchor(leaf, group, leafInner);
                var leafLink = mermaidHorizontalLink(bOut.x, bOut.y, lIn.x, lIn.y, 18);
                edges.add(createPolylineLink(app, leafLink, branchStroke, getActiveDiagram().stroke.edgeThin + 0.25, false));
            }
        }
        catch (e_10_1) { e_10 = { error: e_10_1 }; }
        finally {
            try {
                if (_j && !_j.done && (_a = _h.return)) _a.call(_h);
            }
            finally { if (e_10) throw e_10.error; }
        }
    }
    group.add(edges);
}
function createPolylineLink(app, points, stroke, strokeWidth, glow) {
    var g = app.group({ listening: false });
    if (glow) {
        g.add(app.polyline({
            points: points,
            fill: null,
            stroke: stroke,
            strokeWidth: strokeWidth + 3,
            opacity: 0.2,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
        }));
    }
    g.add(app.polyline({
        points: points,
        fill: null,
        stroke: stroke,
        strokeWidth: strokeWidth,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    }));
    g.metadata.edgePoints = points;
    g.metadata.edgeStroke = stroke;
    return g;
}
